import { useState } from "react";
import { motion } from "framer-motion";
import { Package, Truck, CheckCircle2, AlertTriangle, RotateCcw, IndianRupee, Wallet } from "lucide-react";

const TABS = ["Overview", "Shipments", "Courier Comparison", "COD", "NDR"];
const STATS = [
  { icon: Package, label: "Total Shipments", value: "12,480", tint: "#12C8F5" },
  { icon: Truck, label: "In Transit", value: "1,204", tint: "#FF9933" },
  { icon: CheckCircle2, label: "Delivered", value: "10,812", tint: "#10B981" },
  { icon: AlertTriangle, label: "NDR", value: "214", tint: "#F59E0B" },
  { icon: RotateCcw, label: "RTO", value: "250", tint: "#F43F5E" },
  { icon: IndianRupee, label: "COD Pending", value: "₹4.2L", tint: "#FFB25E" },
  { icon: Wallet, label: "COD Settled", value: "₹38.6L", tint: "#10B981" },
];
const ROWS = [
  ["IF-88213", "Mumbai → Delhi", "Blue Dart", "In Transit", "#FF9933"],
  ["IF-88214", "Hyderabad → Pune", "Delhivery", "Delivered", "#10B981"],
  ["IF-88215", "Chennai → Kolkata", "XpressBees", "NDR", "#F59E0B"],
  ["IF-88216", "Bengaluru → Jaipur", "DTDC", "Delivered", "#10B981"],
];
const BARS = [42, 68, 55, 80, 62, 90, 74, 96, 70, 88, 78, 100];

export default function DashboardPreview() {
  const [tab, setTab] = useState("Overview");
  return (
    <section className="relative overflow-hidden bg-[#030B18] py-20 sm:py-24" data-testid="dashboard-preview">
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-shipany-cyan">Dashboard Preview</span>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">Your Entire Shipping Operation. One Dashboard.</h2>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 50, rotateX: 14 }} whileInView={{ opacity: 1, y: 0, rotateX: 0 }} viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformPerspective: 1400 }}
          className="relative mt-14">
          {/* floating side cards */}
          <div className="animate-float absolute -left-4 -top-8 z-20 hidden rounded-2xl border border-[#10B981]/30 bg-[#0B1E3D]/95 p-4 shadow-2xl backdrop-blur lg:block" data-testid="float-cod-card">
            <p className="mono text-[9px] uppercase tracking-widest text-slate-500">COD Settled</p>
            <p className="mono mt-1 text-lg font-extrabold text-[#10B981]">₹38.6L</p>
          </div>
          <div className="animate-float absolute -right-4 top-1/3 z-20 hidden rounded-2xl border border-[#F59E0B]/30 bg-[#0B1E3D]/95 p-4 shadow-2xl backdrop-blur lg:block" style={{ animationDelay: "1.4s" }} data-testid="float-ndr-card">
            <p className="mono text-[9px] uppercase tracking-widest text-slate-500">NDR Resolved</p>
            <p className="mono mt-1 text-lg font-extrabold text-[#F59E0B]">86%</p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-white/12 bg-[#071B3A]/90 shadow-[0_40px_120px_rgba(0,0,0,0.5)] backdrop-blur-xl">
            <div className="min-w-[760px] p-5 sm:p-7">
              {/* window chrome + tabs */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#F43F5E]" /><span className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" /><span className="h-2.5 w-2.5 rounded-full bg-[#10B981]" />
                </div>
                <div className="flex flex-wrap gap-1 rounded-xl bg-white/5 p-1">
                  {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`dashboard-preview-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-lg px-3 py-1.5 text-[11px] font-bold transition-all ${tab === t ? "bg-gradient-to-r from-[#FF9933] to-[#FF8000] text-[#071B3A] shadow" : "text-slate-400 hover:text-white"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* stat cards */}
              <div className="mt-5 grid grid-cols-4 gap-3 lg:grid-cols-7">
                {STATS.map((s, i) => (
                  <motion.div key={s.label} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="rounded-xl border border-white/8 bg-white/[0.04] p-3 transition-colors hover:border-white/20">
                    <s.icon className="h-4 w-4" style={{ color: s.tint }} />
                    <p className="mono mt-2 text-sm font-extrabold text-white">{s.value}</p>
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{s.label}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-5">
                {/* analytics graph */}
                <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 lg:col-span-2">
                  <p className="mono text-[10px] font-bold uppercase tracking-widest text-slate-400">Weekly Shipments</p>
                  <div className="mt-3 flex h-28 items-end gap-1.5">
                    {BARS.map((b, i) => (
                      <motion.span key={i} initial={{ height: 0 }} whileInView={{ height: `${b}%` }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
                        className="flex-1 rounded-t" style={{ background: i % 3 === 0 ? "#FF9933" : i % 3 === 1 ? "#12C8F5" : "#10B981", opacity: 0.85 }} />
                    ))}
                  </div>
                </div>
                {/* shipment table */}
                <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 lg:col-span-3">
                  <p className="mono text-[10px] font-bold uppercase tracking-widest text-slate-400">Live Shipments</p>
                  <div className="mt-3 space-y-2">
                    {ROWS.map((r) => (
                      <div key={r[0]} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2 transition-colors hover:bg-white/[0.08]">
                        <span className="mono text-[11px] font-bold text-shipany-cyan">{r[0]}</span>
                        <span className="text-[11px] text-slate-300">{r[1]}</span>
                        <span className="hidden text-[11px] text-slate-500 sm:inline">{r[2]}</span>
                        <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ color: r[4], background: `${r[4]}1c` }}>{r[3]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* tracking timeline */}
              <div className="mt-4 rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between">
                  {["Booked", "Picked Up", "In Transit", "Out for Delivery", "Delivered"].map((s, i) => (
                    <div key={s} className="flex flex-1 items-center">
                      <div className="flex flex-col items-center">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${i <= 2 ? "border-[#10B981] bg-[#10B981]/20" : "border-slate-600 bg-transparent"}`}>
                          {i <= 2 && <CheckCircle2 className="h-3 w-3 text-[#10B981]" />}
                        </span>
                        <span className="mt-1 hidden text-[8px] font-bold uppercase tracking-wide text-slate-500 sm:block">{s}</span>
                      </div>
                      {i < 4 && <span className={`mx-1 h-0.5 flex-1 rounded ${i < 2 ? "bg-[#10B981]" : "bg-slate-700"}`} />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
